import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import { TextField, TableFooter, Button } from "@mui/material";
import {
  ExclamationTriangleIcon,
  TrashIcon,
  PlusIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import ProConfirmation from "../../../utils/ProConfirmation";
import { generateRandomString } from "../../../Utils";
import { ChecklistDeviationDialog } from "./checklistDeviation";
import { debounce } from "lodash";
import DocumentsDialog from "../../documents/documents-dialog";
import { DocumentUploadSectionV4 } from "../../Media/DocumentUploadSectionV4";
import { useTranslation } from "react-i18next";
import {
  effectiveActionsForChecklistItem,
  itemRowResolved,
} from "./checklistResponseConfig";
import clsx from "clsx";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  padding: 20,
  margin: `0 0 ${grid}px 0`,

  // change background colour if dragging
  background: isDragging ? "lightgrey" : null,

  // styles we need to apply on draggables
  ...draggableStyle,
});

export default function ChecklistItemLines(props) {
  const { t } = useTranslation();
  const {
    setFieldValue,
    values,
    isLoading,
    name,
    drag,
    editable,
    type,
    checklist,
    disabled,
    deviation,
  } = props;

  const rows = values?.items || props?.items;

  // Reordered items by updating their row_order
  const orderItems = (rows) => {
    const array = [];

    if (!rows) return;

    rows.map((row, i) => {
      // Ensure that the id exists before using it

      // Generate a random string (10 characters)
      const random = generateRandomString();

      const rowParams = {
        ...row,
        row_order: i,
        draggableId: random,
      };

      array.push(rowParams);
    });

    return array;
  };

  // Title column is only used when group_row is true
  const defaultRows = [
    {
      id: null,
      row_order: null,
      description: "",
    },
  ];

  const [items, setItems] = useState(orderItems(rows ? rows : defaultRows));

  const checklistItemNotChecked = () => {
    // Checks all items in the array. All items but be true isApproved OR isDeviation
    const allApproved = items.every((item) => itemRowResolved(item));
    return allApproved;
  };

  useEffect(() => {
    const scrollPosition = window.scrollY; // Save the scroll position

    if (setFieldValue) {
      setFieldValue(name, items);
    }
    window.scrollTo(0, scrollPosition); // Restore the scroll position
  }, [items]);

  const addItemRow = (type) => {
    const newRow = {
      title: "",
      description: "",
      avvik: false,
      row_order: items.length,
      documents: [],
    };
    const updatedItems = [...items, newRow];
    setItems(orderItems(updatedItems));
    // setItems((prevState) => [...prevState, newRow]);
  };

  const onItemRowChange = (e, data) => {
    // setItems(prevState => ({ ...prevState, [1][description]: 'test' }))
    if (typeof e === "function") {
      e.preventDefault();
    }

    let prevItems = [...items];
    let item = {
      ...prevItems[data.row_order],
      [e.target.name]: e.target.value || e.target.checked,
    };
    prevItems[data.row_order] = item;
    setItems(prevItems);
  };

  // Only approved or deviation can be true, not both
  const onDeviationChange = (e, data) => {
    const updatedItems = items.map((item) => {
      if (item.id === data.id || item.draggableId === data.draggableId) {
        return {
          ...item,
          isDeviation: e.target.checked,
          isApproved: false,
        };
      }
      return item;
    });

    setItems(updatedItems);
  };

  const [createDeviationDialogOpen, setCreateDeviationDialogOpen] =
    useState(false);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );
    setItems(orderItems(newItems));
  };

  const [selectedItemRow, setSelectedItemRow] = useState();

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const handleDeleteRow = () => {
    setDeleteConfirmationOpen(false);
    // Delete selectedItem row from array
    if (selectedItemRow) {
      const newArray = items.map((item) =>
        item.id === selectedItemRow ? { ...item, delete: true } : item
      );
      setItems(orderItems(newArray));
    }
  };

  const deleteRow = (id) => {
    setSelectedItemRow(id);
    setDeleteConfirmationOpen(true);
  };

  const UploaderCallback = (newDocuments, row) => {
    // Documents handler
    let prevItems = [...items];

    let item = {
      ...prevItems[row.row_order],
      documents: [...prevItems[row.row_order].documents, ...newDocuments],
    };

    prevItems[row.row_order] = item;

    setItems(prevItems);
  };

  const delayedCallback = debounce(props.parentCallback, 300);

  useEffect(() => {
    // props.parentCallback(items);
    delayedCallback(items);
  }, [items]);

  const [selectedChecklistItem, setSelectedChecklistItem] = useState(null);

  const handleDeviation = (row) => {
    if (!deviation) {
      let item = {};
      // find item by id or row_order if id is null
      if (!row.id) {
        item = items.find((item) => item.id === row.id);
      } else {
        item = items.find((item) => item.row_order === row.row_order);
      }
      onDeviationChange({ target: { checked: true } }, item);
    } else {
      setCreateDeviationDialogOpen(true);
      setSelectedChecklistItem(row);
    }
  };

  const handleDeviationCreate = (data, checklistItem) => {
    // Find item by id
    const item = items.find((item) => item.id === checklistItem.id);

    // Data === Deviation
    // Add the deviation to the item and update items
    let prevItems = [...items];

    let newItem = {
      ...prevItems[item.row_order],
      deviation: data,
      response_code: "deviation",
      isDeviation: true,
      isApproved: false,
      is_not_applicable: false,
    };

    prevItems[item.row_order] = newItem;

    setItems(prevItems);
  };

  useEffect(() => {
    if (!createDeviationDialogOpen) {
      setTimeout(() => {
        setSelectedChecklistItem(null);
      }, 250);
    }
  }, [createDeviationDialogOpen]);

  const canUploadImage = (item) => {
    if (type === "checklist") {
      if (deviation) {
        return !!(item?.isApproved || item?.is_not_applicable);
      }
      return true;
    }
    return false;
  };

  const isResponseSelected = (item, key) => {
    if (item.response_code === key) return true;
    if (!item.response_code) {
      if (
        key === "approved" &&
        item.isApproved &&
        !item.isDeviation &&
        !item.is_not_applicable
      )
        return true;
      if (key === "performed" && item.isApproved && !item.isDeviation && !item.is_not_applicable)
        return true;
      if (key === "deviation" && item.isDeviation) return true;
      if (key === "na" && item.is_not_applicable) return true;
    }
    return false;
  };

  const onResponseSelect = (row, key) => {
    const matchRow = (it) =>
      (row.id != null && it.id === row.id) ||
      it.draggableId === row.draggableId ||
      it.row_order === row.row_order;

    const clear = row.response_code === key;

    if (key === "deviation" && !clear) {
      handleDeviation(row);
      return;
    }

    const patch = clear
      ? {
          response_code: null,
          isApproved: false,
          isDeviation: false,
          is_not_applicable: false,
        }
      : key === "performed" || key === "approved"
        ? {
            response_code: key,
            isApproved: true,
            isDeviation: false,
            is_not_applicable: false,
          }
        : key === "na"
          ? {
              response_code: "na",
              isApproved: false,
              isDeviation: false,
              is_not_applicable: true,
            }
          : {
              response_code: "deviation",
              isApproved: false,
              isDeviation: true,
              is_not_applicable: false,
            };

    setItems((prev) =>
      prev.map((it) => (matchRow(it) ? { ...it, ...patch } : it))
    );
  };

  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);

  const [selectedChecklistItemDocumentId, setSelectedChecklistItemDocumentId] =
    useState(null);

  // Add a row if not rows available in the items array
  useEffect(() => {
    if (items.length === 0) {
      addItemRow();
    }
  }, []);

  const itemRow = (item, provided, snapshot) => {
    if (item.delete) return null; // Hide the item if it is marked as deleted

    return (
      <div
        key={"div" + item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-3"
      >
        {/* Header with drag handle and delete button */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center space-x-2">
            {drag && (
              <div className="text-gray-400">
                <DragHandleIcon className="w-4 h-4" />
              </div>
            )}
            <div className="text-xs text-gray-500 font-medium">
              Item {item.row_order + 1}
            </div>
          </div>

          {props.deleteRow && (
            <button
              onClick={() => deleteRow(item.id)}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200 rounded hover:bg-red-50"
            >
              <TrashIcon className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Title and Description */}
          <div className="space-y-3">
            {!editable && item?.id ? (
              <div className="text-sm font-medium text-gray-900">
                {item?.title}
              </div>
            ) : (
              <TextField
                disabled={false}
                label={t("quality.checklist.item.title")}
                variant="outlined"
                size="small"
                value={item?.title}
                fullWidth
                name="title"
                onChange={(e) => onItemRowChange(e, item)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            )}

            {type === "checklist" && !editable && item?.guidance && (
              <div className="rounded-lg border border-sky-100 bg-sky-50/90 px-3 py-2.5 text-sm text-sky-950">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-800">
                  {t("quality.checklist.item.guidanceTitle")}
                </p>
                <p className="mt-1 whitespace-pre-wrap leading-relaxed">
                  {item.guidance}
                </p>
              </div>
            )}

            {type === "checklist" && (
              <div className="space-y-2">
                <TextField
                  disabled={disabled}
                  label={t("quality.checklist.item.completionNote")}
                  required={item?.isDeviation}
                  variant="outlined"
                  size="small"
                  value={item?.description}
                  fullWidth
                  name="description"
                  multiline
                  rows={2}
                  onChange={(e) => onItemRowChange(e, item)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
                {item?.isDeviation && (
                  <div className="text-xs text-red-500 italic">
                    {t("quality.checklist.item.deviationNoteRequired")}
                  </div>
                )}
              </div>
            )}
          </div>

          {type === "checklist" && canUploadImage(item) && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-600">
                {t("quality.checklist.item.responseLabel")}
              </p>
              <div className="flex flex-wrap gap-2">
                {effectiveActionsForChecklistItem(
                  item,
                  checklist?.checklist_type
                ).map((key) => (
                  <button
                    key={key}
                    type="button"
                    disabled={disabled}
                    onClick={() => onResponseSelect(item, key)}
                    className={clsx(
                      "inline-flex items-center rounded-lg border px-3 py-2 text-xs font-semibold transition-colors",
                      isResponseSelected(item, key)
                        ? "border-teal-600 bg-teal-600 text-white shadow-sm"
                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50",
                      disabled && "cursor-not-allowed opacity-50"
                    )}
                  >
                    {t(`quality.checklist.actions.${key}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Document Upload */}
          {!disabled && canUploadImage(item) && (
            <div className="border-t border-gray-100 pt-3">
              <DocumentUploadSectionV4
                required={
                  (item?.documents?.length || 0) === 0
                    ? item?.documents_required
                    : false
                }
                key={item.id}
                disabled={isLoading}
                module="checklist_item"
                moduleParam={item?.id}
                parentCallback={(data) => UploaderCallback(data, item)}
              />
            </div>
          )}

          {/* View Documents Button */}
          { !editable && (
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                setSelectedChecklistItemDocumentId(item.id);
                setDocumentsDialogOpen(true);
              }}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <DocumentIcon className="w-3 h-3 mr-1" />
              View Documents
              <span className="ml-1 text-xs text-gray-500">
                ({item?.documents?.length || 0})
              </span>
            </button>
          </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <div className="space-y-4">
      {!editable && !checklistItemNotChecked() && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-400" />
            </div>
            <div className="ml-2">
              <h3 className="text-xs font-medium text-red-800">
                Checklist is not ready
              </h3>
              <div className="mt-1 text-xs text-red-700">
                <p>Make sure to check all checklist items</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {items?.map((item, index) => (
                  <Draggable
                    isDragDisabled={!drag}
                    key={"drag" + item.id}
                    draggableId={item.draggableId}
                    index={index}
                  >
                    {(provided, snapshot) => itemRow(item, provided, snapshot)}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Add New Row Button */}
      {props.addRow && !disabled && (
        <div className="flex justify-center pt-2">
          <button
            disabled={disabled}
            className="inline-flex items-center px-4 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            onClick={() => addItemRow()}
          >
            <PlusIcon className="w-3 h-3 mr-1" />
            Add New Item
          </button>
        </div>
      )}

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={"Are you sure you want to delete this row?"}
        action="delete"
        module="order_item"
        yesCallback={() => handleDeleteRow(selectedItemRow)}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />

      <ChecklistDeviationDialog
        open={createDeviationDialogOpen}
        onClose={() => setCreateDeviationDialogOpen(false)}
        checklistItem={selectedChecklistItem}
        checklist={checklist}
        onRefresh={(data, checklistItem) =>
          handleDeviationCreate(data, checklistItem)
        }
      />

      <DocumentsDialog
        open={documentsDialogOpen}
        onClose={() => setDocumentsDialogOpen(false)}
        module="checklist_item"
        moduleParam={selectedChecklistItemDocumentId}
        onRefresh={props.onRefresh}
      />
    </div>
  );
}

ChecklistItemLines.defaultProps = {
  name: "items",
  drag: true,
  delete: true,
  editable: true,
  type: "checklist",
  disabled: false,
  deviation: true,
};
